/**
 * Função para criar rotas CRUD para uma entidade básica (sem aninhamento).
 *
 * @param {string} baseUrl - A URL base da entidade.
 * @returns {object} - Objetos com as rotas de criação, leitura, atualização e exclusão.
 */
function createEntityRoutes(baseUrl) {
  return {
    get: `/${baseUrl}`, // Rota para obter todos os registros da entidade
    getById: (id) => `/${baseUrl}/${id}`, // Rota para obter registro por ID
    create: `/${baseUrl}`, // Rota para criar um novo registro
    update: (id) => `/${baseUrl}/${id}`, // Rota para atualizar um registro por ID
    delete: (id) => `/${baseUrl}/${id}`, // Rota para excluir um registro por ID
  }
}

/**
 * Função para criar rotas CRUD para uma entidade com caminhos aninhados.
 *
 * @param {string} baseUrl - A URL base da entidade principal.
 * @param {string} subPath - O caminho do sub-recurso (por exemplo, "permissions", "roles").
 * @param {boolean} subPathBeforeId - Determina se o sub-path vem antes ou depois do ID da entidade principal.
 * @returns {object} - Objetos com as rotas de criação, leitura, atualização e exclusão para o sub-recurso.
 */
function createNestedRoutes(baseUrl, subPath, subPathBeforeId = false) {
  return {
    get: (id) =>
      subPathBeforeId
        ? `/${baseUrl}/${subPath}${id ? `/${id}` : ''}` // Rota para obter o recurso com o ID (e.g., /users/applications/{applicationId})
        : `/${baseUrl}${id ? `/${id}` : ''}/${subPath}`, // Rota para obter o sub-recurso com o ID da entidade principal (e.g., /users/{userId}/roles)
    getById: (parentId, id) =>
      subPathBeforeId
        ? `/${baseUrl}/${subPath}/${id}` // Rota para obter um sub-recurso específico com seu ID (e.g., /users/applications/{applicationId}) - not directly used by current backend, but kept for consistency
        : `/${baseUrl}/${parentId}/${subPath}/${id}`, // Rota para obter um sub-recurso específico por ID da entidade principal e ID do sub-recurso (e.g., /organizations/{orgId}/addresses/{addressId})
    create: (id) =>
      subPathBeforeId
        ? `/${baseUrl}/${subPath}` // Rota para criar um sub-recurso sem precisar de ID principal (e.g., /users/applications)
        : `/${baseUrl}/${id}/${subPath}`, // Rota para criar um sub-recurso com o ID da entidade principal (e.g., /organizations/{orgId}/addresses)
    update: (parentId, id) =>
      subPathBeforeId
        ? `/${baseUrl}/${subPath}/${id}` // Atualiza sub-recurso. Se subPathBeforeId, 'id' é o ID do sub-recurso (e.g., /applications/{applicationId})
        : !id
          ? `/${baseUrl}/${parentId}/${subPath}`
          : `/${baseUrl}/${parentId}/${subPath}/${id}`, // Rota para atualizar o sub-recurso por ID da entidade principal e ID do sub-recurso (e.g., /organizations/{orgId}/addresses/{addressId}),
    delete: (parentId, id) =>
      subPathBeforeId
        ? `/${baseUrl}/${subPath}/${id}` // Deleta sub-recurso. Se subPathBeforeId, 'id' é o ID do sub-recurso (e.g., /applications/{applicationId})
        : `/${baseUrl}/${parentId}/${subPath}/${id}`, // Rota para excluir o sub-recurso por ID da entidade principal e ID do sub-recurso (e.g., /organizations/{orgId}/addresses/{addressId})
  }
}

/**
 * Define as rotas de várias entidades e seus sub-recursos, criando a estrutura do sistema.
 */
export const entities = {
  login: createEntityRoutes('login'),
  logout: createEntityRoutes('logout'),
  me: createEntityRoutes('me'),
  autocomplete: (model) => `/a/${model}`,

  // Rota para Dashboard, que não é uma entidade CRUD, mas sim uma página de visualização
  dashboard: {
    get: (module) => `/dashboard/${module}`,
    pendingCounts: '/dashboard/pending-counts',
  },

  users: {
    ...createEntityRoutes('users'),
    roles: createNestedRoutes('users', 'roles'),
    rolesUpdate: (userId) => `/users/${userId}/roles`,
    permissions: createNestedRoutes('users', 'permissions'),
    permissionsUpdate: (userId) => `/users/${userId}/permissions`,
    applications: {
      ...createNestedRoutes('users', 'applications'), // This handles /users/{user}/applications/
      organizations: (userId) => `/users/${userId}/applications/organizations`,
      syncMultipleAppOrganizations: (userId) => `/users/${userId}/applications`, // POST /users/{user}/applications/
    },
    organizations: {
      ...createNestedRoutes('users', 'organizations'), // This handles /users/{user}/organizations
      applications: (userId, organizationId) =>
        createNestedRoutes(`users/${userId}/organizations`, 'applications').get(
          organizationId
        ), // Specific route: /users/{user}/organizations/{organization}/applications/
    },
    applicationsAndOrganizations: (userId, applicationId) =>
      `/users/${userId}/applications/${applicationId}/organizations`,
    userAppsAndOrgs: createNestedRoutes('users', 'applications/organizations'), // GET /users/{user}/applications/organizations
  },

  applications: createEntityRoutes('applications'),

  organizations: {
    ...createEntityRoutes('organizations'),
    addresses: createNestedRoutes('organizations', 'addresses'),
    organizationAddress: (organizationId, addressId) =>
      createNestedRoutes(
        `organizations/${organizationId}`,
        `addresses`
      ).getById(organizationId, addressId),
    organizationAddressCreate: (organizationId) =>
      createNestedRoutes(`organizations/${organizationId}`, `addresses`).create(
        organizationId
      ),
    organizationAddressUpdate: (organizationId, addressId) =>
      createNestedRoutes(`organizations/${organizationId}`, `addresses`).update(
        organizationId,
        addressId
      ),
    organizationAddressDelete: (organizationId, addressId) =>
      createNestedRoutes(`organizations/${organizationId}`, `addresses`).delete(
        organizationId,
        addressId
      ),
    addressesLocations: (organizationId, addressId) =>
      createNestedRoutes(
        `organizations/${organizationId}/addresses`,
        'locations'
      ).get(addressId),
    organizationAddressLocations: (organizationId, addressId) =>
      createNestedRoutes(
        `organizations/${organizationId}/addresses`,
        `locations`
      ).get(addressId),
    organizationAddressLocation: (organizationId, addressId, locationId) =>
      createNestedRoutes(
        `organizations/${organizationId}/addresses`,
        `locations`
      ).getById(addressId, locationId),
    organizationAddressLocationCreate: (organizationId, addressId) =>
      createNestedRoutes(
        `organizations/${organizationId}/addresses`,
        `locations`
      ).create(addressId),
    organizationAddressLocationUpdate: (
      organizationId,
      addressId,
      locationId
    ) =>
      createNestedRoutes(
        `organizations/${organizationId}/addresses`,
        `locations`
      ).update(addressId, locationId),
    organizationAddressLocationDelete: (
      organizationId,
      addressId,
      locationId
    ) =>
      createNestedRoutes(
        `organizations/${organizationId}/addresses`,
        `locations`
      ).delete(addressId, locationId),
    contacts: createNestedRoutes('organizations', 'contacts'),
    organizationContact: (organizationId, contactId) =>
      createNestedRoutes(`organizations/${organizationId}`, `contacts`).getById(
        organizationId,
        contactId
      ),
    organizationContactCreate: (organizationId) =>
      createNestedRoutes(`organizations/${organizationId}`, `contacts`).create(
        organizationId
      ),
    organizationContactUpdate: (organizationId, contactId) =>
      createNestedRoutes(`organizations/${organizationId}`, `contacts`).update(
        organizationId,
        contactId
      ),
    organizationContactDelete: (organizationId, contactId) =>
      createNestedRoutes(`organizations/${organizationId}`, `contacts`).delete(
        organizationId,
        contactId
      ),

    equipments: createNestedRoutes('organizations', 'equipments'),
  },

  addresses: {
    ...createEntityRoutes('addresses'),
    locations: createNestedRoutes('addresses', 'locations'),
    addressLocation: (addressId, locationId) =>
      createNestedRoutes(`addresses/${addressId}`, `locations`).getById(
        addressId,
        locationId
      ),
  },

  roles: {
    ...createEntityRoutes('roles'),
    permissions: (roleId) =>
      createNestedRoutes('roles', 'permissions').get(roleId),
    permissionsUpdate: (roleId) =>
      createNestedRoutes('roles', 'permissions').update(roleId),
  },

  permissions: createEntityRoutes('permissions'),

  suppliers: {
    ...createEntityRoutes('suppliers'),
    addresses: createNestedRoutes('suppliers', 'addresses'),
    supplierAddress: (supplierId, addressId) =>
      createNestedRoutes(`suppliers`, `addresses`).getById(
        supplierId,
        addressId
      ),
    supplierAddressCreate: (supplierId) =>
      createNestedRoutes(`suppliers/${supplierId}`, `addresses`).create(
        supplierId
      ),
    supplierAddressUpdate: (supplierId, addressId) =>
      createNestedRoutes(`suppliers`, `addresses`).update(
        supplierId,
        addressId
      ),
    supplierAddressDelete: (supplierId, addressId) =>
      createNestedRoutes(`suppliers`, `addresses`).delete(
        supplierId,
        addressId
      ),
    addressesLocations: createNestedRoutes(
      'suppliers',
      'addresses/{address}/locations'
    ),
    supplierAddressLocations: (supplierId, addressId) =>
      createNestedRoutes(`suppliers/${supplierId}/addresses`, `locations`).get(
        addressId
      ),
    supplierAddressLocationCreate: (supplierId, addressId) =>
      createNestedRoutes(
        `suppliers/${supplierId}/addresses`,
        `locations`
      ).create(addressId),
    supplierAddressLocationUpdate: (supplierId, addressId, locationId) =>
      createNestedRoutes(
        `suppliers/${supplierId}/addresses`,
        `locations`
      ).update(addressId, locationId),
    supplierAddressLocationDelete: (supplierId, addressId, locationId) =>
      createNestedRoutes(
        `suppliers/${supplierId}/addresses`,
        `locations`
      ).delete(addressId, locationId),
    contacts: createNestedRoutes('suppliers', 'contacts'),
    supplierContact: (supplierId, contactId) =>
      createNestedRoutes(`suppliers`, `contacts`).getById(
        supplierId,
        contactId
      ),
    supplierContactCreate: (supplierId) =>
      createNestedRoutes(`suppliers`, `contacts`).create(supplierId),
    supplierContactUpdate: (supplierId, contactId) =>
      createNestedRoutes(`suppliers`, `contacts`).update(supplierId, contactId),
    supplierContactDelete: (supplierId, contactId) =>
      createNestedRoutes(`suppliers`, `contacts`).delete(supplierId, contactId),
  },

  status: createEntityRoutes('status'),
  conditions: createEntityRoutes('conditions'),
  categories: createEntityRoutes('categories'),

  equipments: {
    base: '/equipments',
    ...createEntityRoutes('equipments'),
    allCleaned: createEntityRoutes('equipments/all-cleaned'),
    allCleanedForPeriod: createEntityRoutes(
      'equipments/all-cleaned-for-period'
    ),
    findByNumber: '/equipments/find-by-number',
    lastNumber: '/equipments/last-number',
    byCustomer: createEntityRoutes('equipments/by-customer'),
    groups: (equipmentId) =>
      createNestedRoutes('equipments', 'groups').get(equipmentId),
    equipmentGroups: (equipmentId) =>
      createNestedRoutes(`equipments/${equipmentId}`, `groups`).get(
        equipmentId
      ),
    equipmentGroupsSync: (equipmentId) =>
      createNestedRoutes(`equipments/${equipmentId}`, `groups`).create(
        equipmentId
      ),
    movements: createNestedRoutes('equipments', 'movements'),
    maintenances: createNestedRoutes('equipments', 'maintenances'),
    upgrades: createNestedRoutes('equipments', 'upgrades'),
    createMany: '/equipments/many',
    relabel: createNestedRoutes('equipments', 'relabel'),
    attach: createNestedRoutes('equipments', 'attach'),
    old: createNestedRoutes('equipments', 'old'),
    makeAvailable: createNestedRoutes('equipments', 'make-available'),

    // todos os tipos de equipamentos alugados/reservados
    typeRented: createEntityRoutes('equipments/type-rented'),
    monthly: createEntityRoutes('equipments/monthly'),

    // 🆕 Consulta de garantias
    warranties: {
      get: '/equipments/warranties',
    },
  },

  returnConfirmations: {
    ...createEntityRoutes('return-confirmations'),
  },

  units: {
    ...createEntityRoutes('units'),
    unitsRelated: createNestedRoutes('units', 'units'),
    unitRelated: (unitId, relatedUnitId) =>
      createNestedRoutes(`units/${unitId}`, `units-related`).getById(
        unitId,
        relatedUnitId
      ),
    unitRelatedCreate: (unitId) =>
      createNestedRoutes(`units/${unitId}`, `units-related`).create(unitId),
    unitRelatedUpdate: (unitId, relatedUnitId) =>
      createNestedRoutes(`units/${unitId}`, `units-related`).update(
        unitId,
        relatedUnitId
      ),
    unitRelatedDelete: (unitId, relatedUnitId) =>
      createNestedRoutes(`units/${unitId}`, `units-related`).delete(
        unitId,
        relatedUnitId
      ),
  },

  customers: {
    ...createEntityRoutes('customers'),
    contractants: createEntityRoutes('customers/contractants'),
    addresses: createNestedRoutes('customers', 'addresses'),
    customerAddress: (customerId, addressId) =>
      entities.customers.addresses.getById(customerId, addressId),
    customerAddressCreate: (customerId) =>
      entities.customers.addresses.create(customerId),
    customerAddressUpdate: (customerId, addressId) =>
      entities.customers.addresses.update(customerId, addressId),
    customerAddressDelete: (customerId, addressId) =>
      entities.customers.addresses.delete(customerId, addressId),

    // Rotas de Localização de Endereços (se necessário ajustar, embora não seja o foco atual, seguem o mesmo padrão)
    // Se a API for: /customers/{customer}/addresses/{address}/locations/{location}
    // Você pode precisar de uma estrutura assim, dependendo de como `createNestedRoutes` lida com múltiplos parentes:
    customerAddressLocations: (customerId, addressId) =>
      createNestedRoutes(`customers/${customerId}/addresses`, `locations`).get(
        addressId
      ),
    customerAddressLocationCreate: (customerId, addressId) =>
      createNestedRoutes(
        `customers/${customerId}/addresses`,
        `locations`
      ).create(addressId),
    customerAddressLocationUpdate: (customerId, addressId, locationId) =>
      createNestedRoutes(
        `customers/${customerId}/addresses`,
        `locations`
      ).update(addressId, locationId),
    customerAddressLocationDelete: (customerId, addressId, locationId) =>
      createNestedRoutes(
        `customers/${customerId}/addresses`,
        `locations`
      ).delete(addressId, locationId),

    contacts: createNestedRoutes('customers', 'contacts'),
    customerContact: (customerId, contactId) =>
      entities.customers.contacts.getById(customerId, contactId),
    customerContactCreate: (customerId) =>
      entities.customers.contacts.create(customerId),
    customerContactUpdate: (customerId, contactId) =>
      entities.customers.contacts.update(customerId, contactId),
    customerContactDelete: (customerId, contactId) =>
      entities.customers.contacts.delete(customerId, contactId),

    equipments: createNestedRoutes('customers', 'equipments'),
    movements: createNestedRoutes('customers', 'movements'),

    // todos os clientes com fluxo de aluguel completos
    withRentFlow: createEntityRoutes('customers/with-rent-flow'),
    // todos os clientes que possuem contratos
    withContracts: createEntityRoutes('customers/with-contracts'),
    // todos os parentes de clientes
    parents: createNestedRoutes('customers', 'parents'),
  },

  contractTypes: createEntityRoutes('contracts/types'),
  contractStatus: createEntityRoutes('contracts/status'),
  contractEventTypes: createEntityRoutes('contracts/events/types'),

  destinationOrders: {
    ...createEntityRoutes('destination_orders'),
  },

  contracts: {
    ...createEntityRoutes('contracts'),
    forSelect: '/contracts/select',

    contractAmount: (contractId) =>
      createNestedRoutes('contracts', 'amount').get(contractId),
    infos: (contractId) =>
      createNestedRoutes('contracts', 'infos').get(contractId),
    jobs: (contractId) =>
      createNestedRoutes('contracts', 'jobs').get(contractId),
    // Itens de todos os contratos sem permutas
    items: (contractId) =>
      createNestedRoutes('contracts', 'items').get(contractId),
    itemsAtDate: (contractId) =>
      createNestedRoutes('contracts', 'items/at-date').get(contractId),
    // Itens de todos os contratos com permutas
    allItems: (contractId) =>
      createNestedRoutes('contracts', 'items/all').get(contractId),

    // Rotas de Ordem de Serviço
    serviceOrders: (contractId) =>
      createNestedRoutes('contracts', 'orders').get(contractId),
    contractServiceOrder: (contractId, serviceOrderId) =>
      createNestedRoutes('contracts', 'orders').getById(
        contractId,
        serviceOrderId
      ),
    contractServiceOrderCreate: (contractId) =>
      createNestedRoutes('contracts', 'orders').create(contractId),
    contractServiceOrderUpdate: (contractId, serviceOrderId) =>
      createNestedRoutes('contracts', 'orders').update(
        contractId,
        serviceOrderId
      ),
    contractServiceOrderDelete: (contractId, serviceOrderId) =>
      createNestedRoutes('contracts', 'orders').delete(
        contractId,
        serviceOrderId
      ),
    contractServiceOrderVerifyExceededItems: (contractId) =>
      `/contracts/${contractId}/orders/verify-exceeded-item-quantities`,
    contractServiceOrderItems: (contractId, serviceOrderId) =>
      createNestedRoutes(`contracts/${contractId}/orders`, 'items').get(
        serviceOrderId
      ),
    contractServiceOrderItem: (contractId, serviceOrderId, itemId) =>
      createNestedRoutes(`contracts/${contractId}/orders`, 'items').getById(
        serviceOrderId,
        itemId
      ),
    contractServiceOrderItemCreate: (contractId, serviceOrderId) =>
      createNestedRoutes(`contracts/${contractId}/orders`, 'items').create(
        serviceOrderId
      ),
    contractServiceOrderItemsAdd: (contractId, serviceOrderId) =>
      `/contracts/${contractId}/orders/${serviceOrderId}/items/add-items`,
    contractServiceOrderItemUpdate: (contractId, serviceOrderId, itemId) =>
      createNestedRoutes(`contracts/${contractId}/orders`, 'items').update(
        serviceOrderId,
        itemId
      ),
    contractServiceOrderItemDelete: (contractId, serviceOrderId, itemId) =>
      createNestedRoutes(`contracts/${contractId}/orders`, 'items').delete(
        serviceOrderId,
        itemId
      ),

    // Rotas de Eventos
    events: (contractId) =>
      createNestedRoutes('contracts', 'events').get(contractId),
    contractEventsHistory: (contractId) => `/contracts/${contractId}/events/history`,
    contractEvent: (contractId, eventId) =>
      createNestedRoutes('contracts', 'events').getById(contractId, eventId),
    contractEventCreate: (contractId) =>
      createNestedRoutes('contracts', 'events').create(contractId),
    contractEventUpdate: (contractId, eventId) =>
      createNestedRoutes('contracts', 'events').update(contractId, eventId),
    contractEventDelete: (contractId, eventId) =>
      createNestedRoutes('contracts', 'events').delete(contractId, eventId),

    // Rotas de Aditivos
    eventAdditivesForEdit: (contractId, eventId) =>
      createNestedRoutes(`contracts/${contractId}/events`, 'for-edit').get(
        eventId
      ), // * Usado para a página de edição de eventos de contrato
    eventAdditives: (contractId, eventId) =>
      createNestedRoutes(`contracts/${contractId}/events`, 'additives').get(
        eventId
      ),
    eventAdditive: (contractId, eventId, additiveId) =>
      createNestedRoutes(`contracts/${contractId}/events`, 'additives').getById(
        eventId,
        additiveId
      ),
    eventAdditiveCreate: (contractId, eventId) =>
      createNestedRoutes(`contracts/${contractId}/events`, 'additives').create(
        eventId
      ),
    eventAdditiveUpdate: (contractId, eventId, additiveId) =>
      createNestedRoutes(`contracts/${contractId}/events`, 'additives').update(
        eventId,
        additiveId
      ),
    eventAdditiveDelete: (contractId, eventId, additiveId) =>
      createNestedRoutes(`contracts/${contractId}/events`, 'additives').delete(
        eventId,
        additiveId
      ),

    // !
    eventInfosPrevious: (contractId, eventId) =>
      createNestedRoutes(
        `contracts/${contractId}/events`,
        'infos/previous'
      ).get(eventId),

    // Rotas de Informações de Aditivos
    eventAdditiveInfos: (contractId, eventId, additiveId) =>
      createNestedRoutes(
        `contracts/${contractId}/events/${eventId}/additives`,
        'infos'
      ).get(additiveId),
    eventAdditiveInfoPrevious: (contractId, eventId, additiveId) =>
      createNestedRoutes(
        `contracts/${contractId}/events/${eventId}/additives`,
        'infos/previous'
      ).get(additiveId),
    eventAdditiveInfo: (contractId, eventId, additiveId, infoId) =>
      createNestedRoutes(
        `contracts/${contractId}/events/${eventId}/additives`,
        'infos'
      ).getById(additiveId, infoId),
    eventAdditiveInfoCreate: (contractId, eventId, additiveId) =>
      createNestedRoutes(
        `contracts/${contractId}/events/${eventId}/additives`,
        'infos'
      ).create(additiveId),
    eventAdditiveInfoUpdate: (contractId, eventId, additiveId, infoId) =>
      createNestedRoutes(
        `contracts/${contractId}/events/${eventId}/additives`,
        'infos'
      ).update(additiveId, infoId),
    eventAdditiveInfoDelete: (contractId, eventId, additiveId, infoId) =>
      createNestedRoutes(
        `contracts/${contractId}/events/${eventId}/additives`,
        'infos'
      ).delete(additiveId, infoId),

    // !
    eventItemsPrevious: (contractId, eventId) =>
      createNestedRoutes(
        `contracts/${contractId}/events`,
        'items/previous'
      ).get(eventId),

    // Rotas de Itens de Aditivos
    eventAdditiveItems: (contractId, eventId, additiveId) =>
      createNestedRoutes(
        `contracts/${contractId}/events/${eventId}/additives`,
        'items'
      ).get(additiveId),
    eventAdditiveItemsPrevious: (contractId, eventId, additiveId) =>
      createNestedRoutes(
        `contracts/${contractId}/events/${eventId}/additives`,
        'items/previous'
      ).get(additiveId),
    eventAdditiveItem: (contractId, eventId, additiveId, itemId) =>
      createNestedRoutes(
        `contracts/${contractId}/events/${eventId}/additives`,
        'items'
      ).getById(additiveId, itemId),
    eventAdditiveItemCreate: (contractId, eventId, additiveId) =>
      createNestedRoutes(
        `contracts/${contractId}/events/${eventId}/additives`,
        'items'
      ).create(additiveId),
    eventAdditiveItemUpdate: (contractId, eventId, additiveId, itemId) =>
      createNestedRoutes(
        `contracts/${contractId}/events/${eventId}/additives`,
        'items'
      ).update(additiveId, itemId),
    eventAdditiveItemDelete: (contractId, eventId, additiveId, itemId) =>
      createNestedRoutes(
        `contracts/${contractId}/events/${eventId}/additives`,
        'items'
      ).delete(additiveId, itemId),

    // !
    eventJobs: (contractId, eventId) =>
      createNestedRoutes(`contracts/${contractId}/events`, 'jobs').get(eventId),

    eventJobsPrevious: (contractId, eventId) =>
      createNestedRoutes(`contracts/${contractId}/events`, 'jobs/previous').get(
        eventId
      ),

    // Rotas de Jobs de Aditivos
    eventAdditiveJobs: (contractId, eventId, additiveId) =>
      createNestedRoutes(
        `contracts/${contractId}/events/${eventId}/additives`,
        'jobs'
      ).get(additiveId),
    eventAdditiveJob: (contractId, eventId, additiveId, jobId) =>
      createNestedRoutes(
        `contracts/${contractId}/events/${eventId}/additives`,
        'jobs'
      ).getById(additiveId, jobId),
    eventAdditiveJobCreate: (contractId, eventId, additiveId) =>
      createNestedRoutes(
        `contracts/${contractId}/events/${eventId}/additives`,
        'jobs'
      ).create(additiveId),
    eventAdditiveJobUpdate: (contractId, eventId, additiveId, jobId) =>
      createNestedRoutes(
        `contracts/${contractId}/events/${eventId}/additives`,
        'jobs'
      ).update(additiveId, jobId),
    eventAdditiveJobDelete: (contractId, eventId, additiveId, jobId) =>
      createNestedRoutes(
        `contracts/${contractId}/events/${eventId}/additives`,
        'jobs'
      ).delete(additiveId, jobId),

    // !
    contractRecipients: (contractId) =>
      createNestedRoutes('contracts', 'recipient-customers').get(contractId),
    contractRecipient: (contractId, recipientId) =>
      createNestedRoutes('contracts', 'recipient-customers').getById(
        contractId,
        recipientId
      ),
    contractRecipientCreate: (contractId) =>
      createNestedRoutes('contracts', 'recipient-customers').create(contractId),
    contractRecipientUpdate: (contractId, recipientId) =>
      createNestedRoutes('contracts', 'recipient-customers').update(
        contractId,
        recipientId
      ),
    contractRecipientDelete: (contractId, recipientId) =>
      createNestedRoutes('contracts', 'recipient-customers').delete(
        contractId,
        recipientId
      ),

    contractEquipments: (contractId) =>
      createNestedRoutes('contracts', 'equipments').get(contractId),

    additives: (contractId) =>
      createNestedRoutes(`/contracts`, 'teste').get(contractId),
  },

  serviceOrderdepartments: createEntityRoutes('orders/departments'),
  serviceOrderModeTransports: createEntityRoutes('orders/mode-transports'),
  serviceOrderStatus: createEntityRoutes('orders/status'),
  movementStatus: createEntityRoutes('movements/status'),
  shipmentStatus: createEntityRoutes('shipments/status'),

  serviceOrders: {
    ...createEntityRoutes('orders'),
    list: '/orders/list',
    updateStatus: (id) =>
      createNestedRoutes('orders', 'update-status').update(id),
    items: (serviceOrderId) =>
      createNestedRoutes('orders', 'items').get(serviceOrderId),
    addItems: (serviceOrderId) =>
      createNestedRoutes('orders', 'items/add-items').create(serviceOrderId),
    serviceOrderItem: (serviceOrderId, itemId) =>
      createNestedRoutes(`orders`, `items`).getById(serviceOrderId, itemId),
    serviceOrderItemCreate: (serviceOrderId) =>
      createNestedRoutes(`orders`, `items`).create(serviceOrderId),
    serviceOrderItemUpdate: (serviceOrderId, itemId) =>
      createNestedRoutes(`orders`, `items`).update(serviceOrderId, itemId),
    serviceOrderItemDelete: (serviceOrderId, itemId) =>
      createNestedRoutes(`orders`, `items`).delete(serviceOrderId, itemId),
    serviceOrderItemExtraDelete: (serviceOrderId, itemId, extraId) =>
      `/orders/${serviceOrderId}/items/${itemId}/extras/${extraId}`,
    serviceOrderItemExtraRestore: (serviceOrderId, itemId, extraId) =>
      `/orders/${serviceOrderId}/items/${itemId}/extras/${extraId}`,
    serviceOrderItemStockStatus: (serviceOrderId, itemId) =>
      `/orders/${serviceOrderId}/items/${itemId}/stock-status`,
  },

  separateOrders: {
    ...createEntityRoutes('separate-orders'),
    items: createNestedRoutes('separate-orders', 'items'),
  },

  movementTypes: {
    get: '/movements/types',
    getById: (id) => `/movements/types/${id}`,
    create: '/movements/types',
    update: (id) => `/movements/types/${id}`,
    delete: (id) => `/movements/types/${id}`,
  },

  movementItems: createEntityRoutes('movements/items'),
  movementItemsEquipments: createEntityRoutes('movements/items/equipments'),
  movementItemsVolumes: createEntityRoutes('movements/items/volumes'),

  movements: {
    ...createEntityRoutes('movements'),
    items: createNestedRoutes('movements', 'items'),
    itemEquipments: createNestedRoutes('movements', 'items/equipments'),
    itemProducts: createNestedRoutes('movements', 'items/products'),
    manyItems: createNestedRoutes('movements', 'items/add-many'),
    volumes: createNestedRoutes('movements', 'volumes'),
    volumesUpdate: (movementId) =>
      createNestedRoutes('movements', 'volumes').update(movementId),
    volumeItems: (movementId, volumeId) =>
      createNestedRoutes(`movements/${movementId}/volumes`, 'items').get(
        volumeId
      ), // GET /movements/{movementId}/volumes/{volumeId}/items'
    volumeItemDelete: (movementId, volumeId, itemId) =>
      createNestedRoutes(`movements/${movementId}/volumes`, 'items').delete(
        volumeId,
        itemId
      ), // DELETE /movements/{movementId}/volumes/{volumeId}/items/{itemId}'
    movementItem: (movementId, itemId) =>
      createNestedRoutes(`movements/${movementId}`, `items`).getById(
        movementId,
        itemId
      ),
    movementItemCreate: (movementId) =>
      createNestedRoutes(`movements/${movementId}`, `items`).create(movementId),
    movementItemUpdate: (movementId, itemId) =>
      createNestedRoutes(`movements/${movementId}`, `items`).update(
        movementId,
        itemId
      ),
    movementItemDelete: (movementId, itemId) =>
      createNestedRoutes(`movements/${movementId}`, `items`).delete(
        movementId,
        itemId
      ),
    shipments: createNestedRoutes('movements', 'shipments'),
    movementShipment: (movementId, shipmentId) =>
      createNestedRoutes(`movements`, `shipments`).getById(
        movementId,
        shipmentId
      ),
    movementShipmentCreate: (movementId) =>
      createNestedRoutes(`movements`, `shipments`).create(movementId),
    movementShipmentUpdate: (movementId, shipmentId) =>
      createNestedRoutes(`movements/${movementId}`, `shipments`).update(
        movementId,
        shipmentId
      ),
    movementShipmentDelete: (movementId, shipmentId) =>
      createNestedRoutes(`movements/${movementId}`, `shipments`).delete(
        movementId,
        shipmentId
      ),
    shipmentItems: (movementId, shipmentId) =>
      createNestedRoutes(
        `movements/${movementId}/shipments/${shipmentId}`,
        `items`
      ), // Rota para /movements/{movementId}/shipments/{shipmentId}/items
    movementShipmentItem: (movementId, shipmentId, itemId) =>
      createNestedRoutes(`movements/${movementId}/shipments`, `items`).getById(
        shipmentId,
        itemId
      ), // GET /movements/{movementId}/shipments/{shipmentId}/items/{itemId}
    movementShipmentItemCreate: (movementId, shipmentId) =>
      createNestedRoutes(`movements/${movementId}/shipments`, `items`).create(
        shipmentId
      ), // POST /movements/{movementId}/shipments/{shipmentId}/items
    movementShipmentItemUpdate: (movementId, shipmentId, itemId) =>
      createNestedRoutes(`movements/${movementId}/shipments`, `items`).update(
        shipmentId,
        itemId
      ), // PUT /movements/{movementId}/shipments/{shipmentId}/items/{itemId}
    movementShipmentItemDelete: (movementId, shipmentId, itemId) =>
      createNestedRoutes(`movements/${movementId}/shipments`, `items`).delete(
        shipmentId,
        itemId
      ), // DELETE /movements/{movementId}/shipments/{shipmentId}/items/{itemId}
  },

  shipments: {
    ...createEntityRoutes('shipments'),
    items: createNestedRoutes('shipments', 'items'),
    itemsReplacement: createNestedRoutes('shipments', 'items/to-replacement'),
    shipmentItem: (shipmentId, itemId) =>
      createNestedRoutes(`shipments/${shipmentId}`, `items`).getById(
        shipmentId,
        itemId
      ),
    shipmentItemCreate: (shipmentId) =>
      createNestedRoutes(`shipments/${shipmentId}`, `items`).create(shipmentId),
    shipmentItemUpdate: (shipmentId, itemId) =>
      createNestedRoutes(`shipments/${shipmentId}`, `items`).update(
        shipmentId,
        itemId
      ),
    shipmentItemDelete: (shipmentId, itemId) =>
      createNestedRoutes(`shipments`, `items`).delete(shipmentId, itemId),
    equipments: createNestedRoutes('shipments', 'equipments'),
    products: createNestedRoutes('shipments', 'products'),
    volumes: {
      ...createNestedRoutes('shipments', 'volumes'),
      addVolume: createEntityRoutes('shipments/add-volumes').create,
    },

    customers: (shipmentId) =>
      createNestedRoutes(`shipments`, `customers`).get(shipmentId),
    movements: (shipmentId) =>
      createNestedRoutes(`shipments`, `movements`).get(shipmentId),
    movementTypes: (shipmentId) =>
      createNestedRoutes(`shipments`, `movement-types`).get(shipmentId),

    romaneio: {
      get: (shipmentId) => `/shipments/${shipmentId}/romaneio`,
      create: (shipmentId) => `/shipments/${shipmentId}/romaneio`,
      update: (shipmentId) => `/shipments/${shipmentId}/romaneio`,
      updateOne: (shipmentId, romaneioId) => `/shipments/${shipmentId}/romaneios/${romaneioId}`,
      downloadDocument: (shipmentId, romaneioId, documentId) => `/shipments/${shipmentId}/romaneios/${romaneioId}/documents/${documentId}/download`,
      attachInvoice: (shipmentId) =>
        `/shipments/${shipmentId}/romaneio/attach-invoice`,
    },

    documents: {
      getLatestInvoice: (shipmentId) =>
        `/shipments/${shipmentId}/documents/latest-invoice/download`,
      download: (shipmentId, documentId) =>
        `/shipments/${shipmentId}/documents/${documentId}/download`,
      protocolMatrix: (shipmentId) =>
        `/shipments/${shipmentId}/documents/protocol-matrix`,
      create: (shipmentId) => `/shipments/${shipmentId}/documents`,
      replace: (shipmentId) => `/shipments/${shipmentId}/documents/replace`,
      uploadProtocolo: (shipmentId) =>
        `/shipments/${shipmentId}/documents/upload-protocolo`,
      delete: (shipmentId, documentId) =>
        `/shipments/${shipmentId}/documents/${documentId}`,
    },
  },

  equipmentsKits: {
    ...createEntityRoutes('equipmentsKits'),
    items: createNestedRoutes('equipmentsKits', 'items'),
    equipmentKitItem: (kitId, itemId) =>
      createNestedRoutes(`equipmentsKits`, `items`).getById(kitId, itemId),
    equipmentKitItemCreate: (kitId) =>
      createNestedRoutes(`equipmentsKits`, `items`).create(kitId),
    equipmentKitItemUpdate: (kitId, itemId) =>
      createNestedRoutes(`equipmentsKits`, `items`).update(kitId, itemId),
    equipmentKitItemDelete: (kitId, itemId) =>
      createNestedRoutes(`equipmentsKits`, `items`).delete(kitId, itemId),
  },

  additives: createEntityRoutes('additives'),

  // TODO: Rotas de Insumos do Almoxarifado
  products: {
    ...createEntityRoutes('products'),
    forSelection: createEntityRoutes('products/for-selection'),
    getByCode: '/products/items/find-by-code',
  },

  volumes: {
    ...createEntityRoutes('volumes'),
    getByCode: '/volumes/find-by-code',
    validateItems: (volumeId) => `/volumes/${volumeId}/validate-items`,
  },

  productsItems: createEntityRoutes('products/items'),

  // TODO: Rotas de Marcas do Almoxarifado
  brands: createEntityRoutes('brands'),

  // TODO: Rotas de Grupos do Almoxarifado
  groups: {
    ...createEntityRoutes('groups'),
    // * Subgrupos
    groupsSubGroups: (groupId) =>
      createNestedRoutes('groups', 'subgroups').get(groupId),
    groupsSubGroupsCreate: (groupId) =>
      createNestedRoutes('groups', 'subgroups').create(groupId),
    groupsSubGroupsUpdate: (groupId, subGroupId) =>
      createNestedRoutes('groups', 'subgroups').update(groupId, subGroupId),
    groupsSubGroupsDelete: (groupId, subGroupId) =>
      createNestedRoutes('groups', 'subgroups').delete(groupId, subGroupId),

    // * Tipos
    groupsTypes: (groupId) =>
      createNestedRoutes('groups', 'types').get(groupId),
    groupsTypesCreate: (groupId) =>
      createNestedRoutes('groups', 'types').create(groupId),
    groupsTypesUpdate: (groupId, typeId) =>
      createNestedRoutes('groups', 'types').update(groupId, typeId),
    groupsTypesDelete: (groupId, typeId) =>
      createNestedRoutes('groups', 'types').delete(groupId, typeId),
  },

  // TODO: Rotas de Subgrupos do Almoxarifado
  subgroups: createEntityRoutes('subgroups'),

  // TODO: Rotas de Tipos do Almoxarifado
  types: {
    ...createEntityRoutes('types'),
    // * Grupos
    groups: (typeId) => createNestedRoutes('types', 'groups').get(typeId),
    typeGroups: (typeId) => createNestedRoutes('types', 'groups').get(typeId),
    typeGroupsSync: (typeId) =>
      createNestedRoutes('types', 'groups').create(typeId),
  },

  transactions: {
    ...createEntityRoutes('transactions'),
    types: (transactionId) =>
      createNestedRoutes('transactions', 'types').get(transactionId),
    internalExit: '/transactions/internal-exit',
    type: (transactionId, typeId) =>
      createNestedRoutes('transactions', 'types').getById(
        transactionId,
        typeId
      ),
    typesCreate: (transactionId) =>
      createNestedRoutes('transactions', 'types').create(transactionId),
    typesUpdate: (transactionId, typeId) =>
      createNestedRoutes('transactions', 'types').update(transactionId, typeId),
    typesDelete: (transactionId, typeId) =>
      createNestedRoutes('transactions', 'types').delete(transactionId, typeId),

    items: (transactionId) =>
      createNestedRoutes('transactions', 'items').get(transactionId),
    item: (transactionId, itemId) =>
      createNestedRoutes('transactions', 'items').getById(
        transactionId,
        itemId
      ),
    itemsCreate: (transactionId) =>
      createNestedRoutes('transactions', 'items').create(transactionId),
    itemsUpdate: (transactionId, itemId) =>
      createNestedRoutes('transactions', 'items').update(transactionId, itemId),
    itemsDelete: (transactionId, itemId) =>
      createNestedRoutes('transactions', 'items').delete(transactionId, itemId),
  },

  transactionTypes: createEntityRoutes('transactions/types'),
  documentsTypes: createEntityRoutes('documents/types'),

  maintenanceTypes: createEntityRoutes('maintenance-types'),
  maintenanceStatus: createEntityRoutes('maintenance-status'),
  technicianStatus: createEntityRoutes('technician-status'),
  maintenances: {
    ...createEntityRoutes('maintenances'),
    items: createNestedRoutes('maintenances', 'items'),
    item: (maintenanceId, itemId) =>
      createNestedRoutes('maintenances', 'items').getById(
        maintenanceId,
        itemId
      ),
    itemCreate: (maintenanceId) =>
      createNestedRoutes('maintenances', 'items').create(maintenanceId),
    itemDocumentCreate: (itemId) => `/maintenances/items/${itemId}/documents`,
    itemDocumentDownload: (maintenanceItemId, documentId) =>
      `/maintenances/items/${maintenanceItemId}/documents/${documentId}/download`,
    itemUpgradeApproval: (itemId) => `/maintenance-items/${itemId}/upgrade-approval`,
    close: (maintenanceId) =>
      createNestedRoutes('maintenances', 'close').create(maintenanceId),
  },

  billing: {
    ...createEntityRoutes('billings'),
    data: createEntityRoutes('billings/data'),
    items: createNestedRoutes('billings', 'items'),
    byContract: (contractId) =>
      createEntityRoutes('billings/by-contract').getById(contractId),
    updateStatus: (id) =>
      createNestedRoutes('billings', 'update-status').update(id),
  },

  // Relatórios, Protocolos e Etiquetas
  reports: {
    contracts: createEntityRoutes('reports/contracts'),
    equipmentByContract: createEntityRoutes('reports/equipment-by-contract'),
    equipmentByCustomer: createEntityRoutes('reports/equipment-by-customer'),
    equipmentInStock: createEntityRoutes('reports/equipment-in-stock'),
    checkStatus: (id) => `/reports/check-status/${id}`,
    cancel: (id) => `/reports/cancel/${id}`,
    maintenances: createEntityRoutes('reports/maintenances'),
    logistics: createEntityRoutes('reports/logistics'),
    performance: createEntityRoutes('reports/performace-technicians'),
    protocol: (movementId) =>
      createEntityRoutes('reports/protocol').getById(movementId),
    protocolTransaction: (transactionId) =>
      createEntityRoutes('reports/protocol-transaction').getById(transactionId),
    // Protocolo de Saída Interna
    internalExitProtocol: (transactionId) =>
      createEntityRoutes('reports/internal-exit-protocol').getById(
        transactionId
      ),
    // Gerar etiquetas de equipamentos a partir de um movimento
    tag: (movementId) => createEntityRoutes('reports/tag').getById(movementId),

    tagVolumes: (movementId) =>
      createEntityRoutes('reports/tag/volumes').getById(movementId),
    // Gerar etiqueta de volume de um equipamento específico do movimento
    tagVolumeEquipment: (movementId) =>
      `${createEntityRoutes('reports/tag/volumes').getById(movementId)}/equipment`,
    // Gerar as etiquetas a partir do produto
    tagProduct: (productId) =>
      createEntityRoutes('reports/tag/product').getById(productId),
    // Gerar as etiquetas a partir de uma transação
    tagProductFromTransaction: (transactionId) =>
      createEntityRoutes('reports/tag/product/transaction').getById(
        transactionId
      ),

    // * Relatórios do almoxarifado
    transactions: createEntityRoutes('reports/transactions'),
    accounting: createEntityRoutes('reports/accounting'),
    productExtract: createEntityRoutes('reports/product-extract'),
    stockControlCard: createEntityRoutes('reports/stock-control-card'),
    monthlyAverageConsumption: createEntityRoutes(
      'reports/monthly-average-consumption'
    ),
    inventory: createEntityRoutes('reports/inventory'),
    criticalStock: createEntityRoutes('reports/critical-stock'),
  },
}
